export type TabStatus = "idle" | "working" | "done" | "error";

export interface ResolvedTabStatus {
  status: TabStatus;
  count: number;
  titlePrefix: string;
}

export interface TabStatusDomSnapshot {
  favicon: HTMLLinkElement | null;
  href: string | null;
  type: string | null;
  sizes: string | null;
  title: string;
}

const STATUS_ICON_HREFS: Record<Exclude<TabStatus, "idle">, string> = {
  working: "/icons/tab-status/working.png",
  done: "/icons/tab-status/done.png",
  error: "/icons/tab-status/error.png",
};

// Second frame of the "working" blink; the hook swaps it with the working icon.
const WORKING_DIM_ICON_HREF = "/icons/tab-status/working-dim.png";

export const WORKING_BLINK_INTERVAL_MS = 1000;

export function resolveTabStatus(summary: string, tabVisible: boolean): ResolvedTabStatus {
  const [rawStatus, rawCount] = summary.split("|");
  const count = Number.parseInt(rawCount ?? "0", 10) || 0;

  if (rawStatus === "error") {
    return { status: "error", count, titlePrefix: "(!) " };
  }
  if (rawStatus === "done") {
    return { status: "done", count, titlePrefix: `(${count}) ` };
  }
  if (rawStatus === "working") {
    return {
      status: "working",
      count: 0,
      titlePrefix: tabVisible ? "" : "Working - ",
    };
  }
  return { status: "idle", count: 0, titlePrefix: "" };
}

export function captureTabStatusDom(): TabStatusDomSnapshot {
  const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  return {
    favicon,
    href: favicon?.getAttribute("href") ?? null,
    type: favicon?.getAttribute("type") ?? null,
    sizes: favicon?.getAttribute("sizes") ?? null,
    title: document.title,
  };
}

export function applyTabStatus(
  snapshot: TabStatusDomSnapshot,
  resolved: ResolvedTabStatus,
  baseTitle: string,
): string {
  if (resolved.status === "idle") {
    restoreTabStatus(snapshot, baseTitle);
    return baseTitle;
  }

  snapshot.favicon?.setAttribute("href", STATUS_ICON_HREFS[resolved.status]);
  const nextTitle = `${resolved.titlePrefix}${baseTitle}`;
  document.title = nextTitle;
  return nextTitle;
}

/** Shows one frame of the "working" blink: the dim icon when `dim`, else the normal working icon. */
export function applyWorkingFrame(snapshot: TabStatusDomSnapshot, dim: boolean): void {
  snapshot.favicon?.setAttribute("href", dim ? WORKING_DIM_ICON_HREF : STATUS_ICON_HREFS.working);
}

/** True when the favicon currently shows the dim "working" frame. */
export function isWorkingDimFrame(snapshot: TabStatusDomSnapshot): boolean {
  return snapshot.favicon?.getAttribute("href") === WORKING_DIM_ICON_HREF;
}

function restoreAttribute(
  element: HTMLElement,
  name: string,
  value: string | null,
): void {
  if (value == null) {
    element.removeAttribute(name);
    return;
  }
  element.setAttribute(name, value);
}

export function restoreTabStatus(
  snapshot: TabStatusDomSnapshot,
  baseTitle: string = snapshot.title,
): void {
  if (snapshot.favicon) {
    restoreAttribute(snapshot.favicon, "href", snapshot.href);
    restoreAttribute(snapshot.favicon, "type", snapshot.type);
    restoreAttribute(snapshot.favicon, "sizes", snapshot.sizes);
  }
  document.title = baseTitle;
}
